import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { GoldAddressService } from './gold-address.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

const STEP_UPDATE = 'update';
const STEP_CHANGE = 'change';
const STEP_NEW = 'new';

@Component({
    selector: 'gtw-gold-address',
    templateUrl: './gold-address.component.html',
    styleUrls: ['./gold-address.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GoldAddressComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  minimise = false;
  crudLoading = false;
  loading = true;
  step = STEP_UPDATE;
  title = '';

  baseURLs: any = {};
  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  countries: any[] = [];
  states: any[] = [];

  address: any = {};
  selectedAddress: any = null;
  addressSearchText = '';
  addressSearchResults: any[] = [];
  addressSearching = false;
  addressSet = false;

  irsAddress = false;
  entityLinkDisabled = true;
  entityTypeLookUpSelected = false;
  entityTypeBlankSelected = false;
  entityTypeCurrentSelected = true;
  entitySet = false;
  entitySearchText = '';
  entitySearchResults: any[] = [];
  entitySearching = false;

  legalEntityString = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private goldService: GoldAddressService,
    private gtwSnackbar: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.countries = this.GENERAL_CONFIG?.countries || (header.globalParams?.getCountries?.() ?? []) || [];
    this.states = this.GENERAL_CONFIG?.states || (header.globalParams?.getStates?.() ?? []) || [];
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      const rd = this.rowData;
      this.legalEntityString = (rd.legal_entity_id ?? rd.LEGAL_ENTITY_ID ?? '') + ' - ' + (rd.legal_entity_name ?? rd.LEGAL_ENTITY_NAME ?? '');
      this.title = 'Address Change: ' + this.legalEntityString;
    }
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    const addrId = this.rowData.ADDRESS_ID ?? this.rowData.address_id;
    if (addrId != null && addrId !== '') {
      this.loadAddress(addrId);
    } else {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private loadAddress(addressId: string | number): void {
    this.loading = true;
    this.cdr.detectChanges();
    this.goldService.loadAddress(
      this.GENERAL_CONFIG.base_url,
      addressId,
      { globalParams: this.globalService },
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (addr) => {
        this.address = addr || {};
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  changeStep(s: string): void {
    this.step = s;
    if (s === STEP_NEW) {
      this.address = {
        address_id: '', address_line1: '', address_line2: '', address_city: '', country: '', state: '',
        address_start_date: '1998-01-01', address_end_date: '9999-12-31',
        irs_address_line1: '', irs_address_line2: '', irs_address_city: '',
        OBJECT_ID: this.rowData?.key ?? this.rowData?.KEY
      };
      this.irsAddress = false;
    }
    this.cdr.detectChanges();
  }

  setIrsAddress(): void {
    if (this.irsAddress) {
      this.address.irs_address_line1 = this.address.address_line1;
      this.address.irs_address_line2 = this.address.address_line2;
      this.address.irs_address_city = this.address.address_city;
    } else {
      this.address.irs_address_line1 = '';
      this.address.irs_address_line2 = '';
      this.address.irs_address_city = '';
    }
    this.cdr.detectChanges();
  }

  updateAddress(): void {
    if (this.irsAddress) {
      this.address.irs_address_line1 = this.address.address_line1;
      this.address.irs_address_line2 = this.address.address_line2;
      this.address.irs_address_city = this.address.address_city;
    }
    this.cdr.detectChanges();
  }

  searchAddress(): void {
    if (!this.addressSearchText || this.addressSearchText.length < 2) return;
    this.addressSearching = true;
    this.cdr.detectChanges();
    this.goldService.searchAddress(this.GENERAL_CONFIG.base_url, this.addressSearchText).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.addressSearchResults = list || [];
        this.addressSearching = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.addressSearching = false;
        this.cdr.detectChanges();
      }
    });
  }

  setAddress(addr: any): void {
    this.selectedAddress = addr;
    this.addressSet = true;
    this.addressSearchResults = [];
    this.cdr.detectChanges();
  }

  clearAddressSelection(): void {
    this.selectedAddress = null;
    this.addressSet = false;
    this.cdr.detectChanges();
  }

  searchEntity(): void {
    if (!this.entitySearchText || this.entitySearchText.length < 2) return;
    this.entitySearching = true;
    this.cdr.detectChanges();
    this.goldService.searchEntity(this.GENERAL_CONFIG.base_url, this.entitySearchText).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.entitySearchResults = list || [];
        this.entitySearching = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.entitySearching = false;
        this.cdr.detectChanges();
      }
    });
  }

  setEntity(ent: any): void {
    this.entitySet = true;
    this.address.OBJECT_ID = ent?.key ?? ent?.KEY;
    this.entitySearchResults = [];
    this.cdr.detectChanges();
  }

  clearLinkedEntity(): void {
    this.entitySet = false;
    this.address.OBJECT_ID = null;
    this.cdr.detectChanges();
  }

  setLookupEntityLinkType(): void {
    this.entityTypeBlankSelected = false;
    this.entityTypeCurrentSelected = !this.entityTypeLookUpSelected;
    this.entityLinkDisabled = !this.entityTypeLookUpSelected;
    if (!this.entityTypeLookUpSelected) {
      this.address.OBJECT_ID = this.rowData?.key ?? this.rowData?.KEY;
    }
    this.cdr.detectChanges();
  }

  setCurrentEntityLinkType(): void {
    if (this.entityTypeCurrentSelected) {
      this.entityTypeLookUpSelected = false;
      this.entityTypeBlankSelected = false;
      this.entityLinkDisabled = true;
      this.address.OBJECT_ID = this.rowData?.key ?? this.rowData?.KEY;
    }
    this.cdr.detectChanges();
  }

  setBlankEntityLinkType(): void {
    if (this.entityTypeBlankSelected) {
      this.entityTypeLookUpSelected = false;
      this.entityTypeCurrentSelected = false;
      this.entityLinkDisabled = true;
      this.address.OBJECT_ID = null;
    } else {
      this.entityTypeCurrentSelected = true;
      this.address.OBJECT_ID = this.rowData?.key ?? this.rowData?.KEY;
    }
    this.cdr.detectChanges();
  }

  save(step: string): void {
    if (this.crudLoading) return;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const gs = { globalParams: this.globalService };
    const fp = this.filterParams;
    const globalServiceRef = gs;

    const done = (msg: string) => {
      this.crudLoading = false;
      this.gtwSnackbar.openSnackBar(msg, 'gtw-snackbar--green', true, 4);
      this.requestGridUpdate.emit('refresh');
      this.cdr.detectChanges();
    };
    const err = () => {
      this.crudLoading = false;
      this.gtwSnackbar.openSnackBar('An error has occurred. If this error continues please go to the help page and contact support.', 'gtw-snackbar--red', true, 4);
      this.cdr.detectChanges();
    };

    if (step === STEP_UPDATE) {
      this.goldService.saveUpdate(baseUrl, this.address, globalServiceRef, fp).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (r) => (r?.callSuccess === '1') ? done('Address saved Successfully.') : err(),
        error: err
      });
    } else if (step === STEP_CHANGE) {
      const addrId = this.selectedAddress?.address_id ?? this.selectedAddress?.address_string?.address_id;
      if (addrId == null) {
        this.gtwSnackbar.openSnackBar('Please select an address.', 'gtw-snackbar--yellow', true, 4);
        this.crudLoading = false;
        this.cdr.detectChanges();
        return;
      }
      this.goldService.saveChange(baseUrl, this.rowData, addrId, globalServiceRef, fp).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (r) => (r?.callSuccess === '1') ? done('Address changed Successfully.') : err(),
        error: err
      });
    } else if (step === STEP_NEW) {
      if (this.entityTypeLookUpSelected && (this.address.OBJECT_ID == null || this.address.OBJECT_ID === '')) {
        this.gtwSnackbar.openSnackBar('Please Lookup and Select Entity', 'gtw-snackbar--yellow', true, 4);
        this.crudLoading = false;
        this.cdr.detectChanges();
        return;
      }
      this.goldService.saveNew(baseUrl, this.address, globalServiceRef, fp).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (r) => (r?.callSuccess === '1') ? done('Address Created Successfully.') : err(),
        error: err
      });
    }
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
